/**
 * Zero-Memory Streaming ZIP Export utility using client-zip.
 *
 * Pulls binary assets directly from OPFS sequentially, feeding them into
 * downloadZip without hoarding all files in high-pressure main thread memory.
 */

import { downloadZip } from 'client-zip'
import { readFromOPFS } from './storage/opfs'
import type { FileRecord } from './storage/types'

export interface ZipExportProgress {
  current: number
  total: number
  filename: string
}

export async function exportRecordsToZip(
  records: FileRecord[],
  zipFilename = 'nitido-optimizados.zip',
  onProgress?: (progress: ZipExportProgress) => void,
): Promise<void> {
  const completedRecords = records.filter(
    (r): r is FileRecord & { results: NonNullable<FileRecord['results']> } =>
      r.status === 'done' && Array.isArray(r.results) && r.results.length > 0,
  )
  if (completedRecords.length === 0) {
    throw new Error('No hay archivos optimizados listos para descargar.')
  }

  // Calculate total items to export
  const totalEntries = completedRecords.reduce((acc, r) => acc + r.results.length, 0)
  let processedCount = 0

  const entries: { name: string; input: ArrayBuffer; lastModified: Date }[] = []

  for (const record of completedRecords) {
    const baseFolder = record.name.replace(/\.[^/.]+$/, '')

    for (const result of record.results) {
      processedCount++
      if (onProgress) {
        onProgress({
          current: processedCount,
          total: totalEntries,
          filename: result.filename,
        })
      }

      const buffer = await readFromOPFS(result.opfsPath)
      if (buffer) {
        // If single file with single result, don't nest inside deep subfolders
        const filePath =
          completedRecords.length === 1 && record.results.length === 1
            ? result.filename
            : `${baseFolder}/${result.filename}`

        entries.push({
          name: filePath,
          input: buffer,
          lastModified: new Date(record.updatedAt || Date.now()),
        })
      }
    }
  }

  if (entries.length === 0) {
    throw new Error('Los archivos binarios no se encontraron en el almacenamiento local OPFS.')
  }

  const response = downloadZip(entries)
  const blob = await response.blob()

  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = zipFilename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)

  // Revoke blob URL after slight delay to allow download trigger
  setTimeout(() => URL.revokeObjectURL(url), 4000)
}

/**
 * Downloads a single processed result directly from OPFS.
 */
export async function downloadSingleResult(
  opfsPath: string,
  filename: string,
): Promise<void> {
  const buffer = await readFromOPFS(opfsPath)
  if (!buffer) {
    throw new Error(`Archivo no disponible en almacenamiento local: ${opfsPath}`)
  }

  const blob = new Blob([buffer])
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)

  setTimeout(() => URL.revokeObjectURL(url), 2000)
}
